<template>
  <div>
    <div
      v-show="displayFields?.length"
      class="displayFieldsWrapper"
    >
      <template
        v-for="(displayField, index) in displayFields"
        :key="displayField.uuid"
      >
        <AddressDisplay
          v-if="displayField.unit === 'address'
            && (displayField.displayFields?.some((displayField) => displayField.value) || editing)"
          :display-field="displayField"
          :editing="editing"
          @updateDisplayField="(newField) => $emit('updateDisplayField', newField, displayField)"
          @deleteDisplayField="handleDeleteDisplayField(displayField, index)"
        />
        <PickerRow
          v-else-if="displayField.unit === 'picker' && shouldShowDisplayField(displayField)"
          :display-field="displayField"
          :editing="editing"
          @updateDisplayField="(newField) => $emit('updateDisplayField', newField, displayField)"
          @deleteDisplayField="handleDeleteDisplayField(displayField, index)"
        />
        <MultiPickerRow
          v-else-if="displayField.unit === 'multiPicker' && shouldShowDisplayField(displayField)"
          :display-field="displayField"
          :editing="editing"
          @updateDisplayField="(newField) => $emit('updateDisplayField', newField, displayField)"
          @deleteDisplayField="handleDeleteDisplayField(displayField, index)"
        />
        <DateRow
          v-else-if="displayField.unit === 'date' && shouldShowDisplayField(displayField)"
          :display-field="displayField"
          :editing="editing"
          @updateDisplayField="(newField) => $emit('updateDisplayField', newField, displayField)"
          @deleteDisplayField="handleDeleteDisplayField(displayField, index)"
        />
        <FormattedRow
          v-else-if="shouldShowDisplayField(displayField)"
          :display-field="displayField"
          :editing="editing"
          @updateDisplayField="(newField) => $emit('updateDisplayField', newField, displayField)"
          @deleteDisplayField="handleDeleteDisplayField(displayField, index)"
        />
      </template>
    </div>
    <div
      v-show="!displayFields.length"
      class="emptyDisplayFieldsSpacer"
    />
    <NewFieldRow
      v-if="editing"
      :comp-group-type="compGroupType"
      @addDisplayField="(newField) => $emit('addDisplayField', newField)"
    />
  </div>
</template>

<script>
// Components
import AddressDisplay from './AddressDisplay.vue';
import FormattedRow from './FormattedRow.vue';
import PickerRow from './PickerRow.vue';
import MultiPickerRow from './MultiPickerRow.vue';
import DateRow from './DateRow.vue';

// Tools & Services
import {mapMutations} from 'vuex';
import {mappedFields} from '@/lib/StandardFields';
import NewFieldRow from './NewFieldRow.vue';
import {Link} from '@/lib/classes/Link';

/** @typedef {import('@/lib/classes/InfoWindowContent').DisplayField} DisplayField */

export default {
  name: 'DisplayFields',
  components: {
    AddressDisplay,
    FormattedRow,
    NewFieldRow,
    PickerRow,
    MultiPickerRow,
    DateRow,
  },
  props: {
    displayFields: {
      type: Array,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    compGroupType: {
      type: String,
      default: '',
    },
  },
  emits: ['addDisplayField', 'updateDisplayField', 'deleteDisplayField'],
  methods: {
    ...mapMutations(['setSnack']),
    shouldShowDisplayField(displayField) {
      // If we are editing, every possible display field should show
      if (this.editing) return true;

      // If we are not editing show rows that have values
      const invalidValues = [undefined, null, ''];
      if (displayField.value instanceof Link) {
        return !invalidValues.includes(displayField.value.url);
      }
      if (!invalidValues.includes(displayField.value)) {
        return true;
      }
      const isStandardField = Object.values(mappedFields).flat() // get an array of all possible standard fields
        .some((field) => field.label === displayField.label); // match on any field label
      // Show the row for valid labels that are not standard fields, even if no value exists
      if (!invalidValues.includes(displayField.label) && !isStandardField) {
        return true;
      }

      // do not show by default
      return false;
    },
    /**
     * @param {DisplayField} displayFieldToDelete
     * @param {number} index
     */
    handleDeleteDisplayField(displayFieldToDelete, index) {
      this.setSnack({
        message: 'Field has been removed',
        action: {
          text: 'Undo',
          callback: () => this.$emit('addDisplayField', displayFieldToDelete, index),
        },
      });

      this.$emit('deleteDisplayField', displayFieldToDelete);
    },
  },
};
</script>

<style lang="scss" scoped>
.displayFieldsWrapper {
  width: inherit;
  display: flex;
  flex-direction: column;
  gap: 2px; // using rem here causes inconsistent spacing
  margin-bottom: 4px;
  cursor: text;
}

.emptyDisplayFieldsSpacer {
  margin-top: 2px;
}
</style>
